import React, { useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router'
import { toast } from 'react-toastify'
import LoadingContainer from 'components/LoadingContainer'
import { usePrepareCart } from 'hooks/usePrepareCart'
import useSignedUser from 'hooks/useSignedUser'
import { trackRecurrency } from '@cannect/services/events'
import { CART_LINK_CYCLE_PARAM, CART_LINK_ERROR } from '@cannect/services/resources/recurrency/constants'
import { GENERIC_ERROR_MESSAGE, parseCartLinkCycle } from '@cannect/services/resources/recurrency/helpers'
import { isRecurrencyEnabled } from '@cannect/utils/featureFlags'
import PageCartProducts from './pageCartProducts'

function NewCart() {
  const { getLastOrder, loading, lastOrderCreated, claimCartLink, setCartLinkClaimStatus, setRecurrencyCycleDays } =
    usePrepareCart()
  const { signedUser } = useSignedUser()
  const history = useHistory()
  const { pathname, search } = useLocation()
  const claimedCartRef = useRef<string | null>(null)

  useEffect(() => {
    getLastOrder()
  }, [])

  // 010 (research R8): de volta do login ao carrinho do link, vincula o pedido à
  // conta uma vez por carrinho e aplica o ciclo escolhido antes do login.
  const params = new URLSearchParams(search)
  const cartId = params.get('cart_id')
  const offer = lastOrderCreated?.recurrencyOffer
  const orderMatchesLink = Boolean(cartId) && (lastOrderCreated?.uuid ? lastOrderCreated.uuid === cartId : true)

  useEffect(() => {
    if (loading || !cartId || !signedUser?.id || !isRecurrencyEnabled()) return
    if (!orderMatchesLink || !offer?.available || offer.ownedByCurrentUser) return
    if (claimedCartRef.current === cartId) return
    claimedCartRef.current = cartId

    const dropCycleParam = () => {
      if (!params.has(CART_LINK_CYCLE_PARAM)) return
      const next = new URLSearchParams(search)
      next.delete(CART_LINK_CYCLE_PARAM)
      history.replace({ pathname, search: `?${next.toString()}` })
    }

    const run = async () => {
      const result = await claimCartLink()

      if (result.ok) {
        trackRecurrency('recurrency_cart_link_claim', { origin: 'checkout', source: 'cart_link', result: 'ok' })
        const cycle = parseCartLinkCycle(params.get(CART_LINK_CYCLE_PARAM), offer.cycleOptions)
        if (cycle) await setRecurrencyCycleDays(cycle)
        dropCycleParam()
        return
      }

      switch (result.code) {
        case CART_LINK_ERROR.PERSON_MISMATCH:
          setCartLinkClaimStatus('mismatch')
          trackRecurrency('recurrency_cart_link_claim', { origin: 'checkout', source: 'cart_link', result: 'mismatch' })
          break
        case CART_LINK_ERROR.OTHER_USER:
          setCartLinkClaimStatus('other_user')
          trackRecurrency('recurrency_cart_link_claim', {
            origin: 'checkout',
            source: 'cart_link',
            result: 'other_user'
          })
          break
        case CART_LINK_ERROR.NOT_CLAIMABLE:
          // Pedido pago/cancelado no meio do caminho: comportamento atual.
          setCartLinkClaimStatus('idle')
          getLastOrder(false)
          break
        case CART_LINK_ERROR.ORIGIN_NOT_ALLOWED:
        case CART_LINK_ERROR.FEATURE_DISABLED:
          setCartLinkClaimStatus('idle')
          break
        default:
          // Rede, 5xx ou PROFESSIONAL_ACCOUNT (não deve mais ocorrer, R7). Uma nova
          // tentativa só no próximo carregamento da página.
          setCartLinkClaimStatus('error')
          toast.error(GENERIC_ERROR_MESSAGE)
      }
      dropCycleParam()
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uma tentativa por carrinho (claimedCartRef)
  }, [loading, cartId, signedUser?.id, orderMatchesLink, offer?.available, offer?.ownedByCurrentUser])

  if (loading) return <LoadingContainer loading />

  return <PageCartProducts />
}

export default NewCart
